import React, { useCallback, useEffect, useState } from 'react'
import { Message } from 'rsuite'

// Import custom components
import VersatileGrid from '../../ui_reusable/VersatileGrid'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import { VerySmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import {
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import useStandardToaster from '../../ui_reusable/StandardToaster'

// Import context and utils
import { useProjectContext } from '../../../context/ProjectContext'

// Import styled components
import { StatsContainer } from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { HeaderMedium600Style } from '../../../styles/Text.styles'
import { useClientContext } from '../../../context/ClientContext'

const PAGE_SIZE = 10

const ModelHistoryPage = () => {
  const [artifacts, setArtifacts] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null) // { key (backend field), direction } | null
  const [loading, setLoading] = useState(true)
  const { selectedClient } = useClientContext()
  const { selectedProject } = useProjectContext()
  const { pushToast } = useStandardToaster()

  // FIX: Added AbortController to prevent race condition where a slow unfiltered request
  // could complete after a faster filtered request, overwriting correct data with stale data.
  // When selectedClient/selectedProject change, the previous request is aborted.
  const clientId = selectedClient?.id
  const projectId = selectedProject?.id

  // Global filter changes invalidate the current page
  useEffect(() => {
    setPage(1)
  }, [clientId, projectId])

  useEffect(() => {
    const abortController = new AbortController()

    const fetchArtifacts = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })

        if (clientId && projectId) {
          params.set('client_id', String(clientId))
          params.set('project_id', String(projectId))
        }

        if (search) {
          params.set('search', search)
        }

        if (sort) {
          params.set(
            'ordering',
            `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
          )
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/artifacts/history/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages — step back and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const result = await response.json()

        setArtifacts(result.data || [])
        setTotal(result.total ?? result.data?.length ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          pushToast(<Message type="error">Failed to load history</Message>, {
            placement: 'topCenter',
            duration: 5000,
          })
          setArtifacts([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchArtifacts()

    return () => {
      abortController.abort()
    }
    /* eslint-disable-next-line */
  }, [clientId, projectId, page, search, sort])

  const headers = [
    {
      title: 'Run date',
      key: 'run_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      serverKey: 'run_at',
    },
    {
      title: 'Dependent variable',
      key: 'dependent_variable',
      type: 'string',
      isSortable: false,
      isSearchable: true,
      center: true,
    },
    {
      title: 'Variables',
      key: 'variables_count',
      type: 'number',
      isSortable: true,
      isSearchable: false,
      center: true,
      serverKey: 'variables',
    },
    {
      title: 'Models',
      key: 'model',
      type: 'string',
      isSortable: false,
      isSearchable: true,
      center: true,
    },
    {
      title: 'Fit score',
      key: 'fit_score_quality', // Changed to use the string quality status
      type: 'string', // Important: Use string type for searching text values
      isSortable: true,
      isSearchable: false,
      center: true,
      // Use fit_score for actual numeric sorting
      getSortValue: (row) => row.fit_score,
      serverKey: 'fit_score',
    },
    {
      title: 'Run by',
      key: 'created_by',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  const gridHeader = (
    <StatsContainer>
      <span css={HeaderMedium600Style}>Model History: {total}</span>
    </StatsContainer>
  )

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  const handleSortChange = useCallback((nextSort) => {
    setPage(1)
    setSort(nextSort)
  }, [])

  return (
    <>
      <HeaderContainer>
        <SectionTitle>Model History</SectionTitle>
        <ClientProjectPicker />
      </HeaderContainer>

      <SimpleAccordion
        id={'modelHistoryPageAccordion'}
        defaultActiveKey={['1']}
      >
        <SimpleAccordion.Panel eventKey="1" noCollapse>
          <VersatileGrid
            gridContainerStyle={{
              gridTemplateColumns: '1fr 1fr 1fr 1fr 0.7fr 1fr',
            }}
            headers={headers}
            data={artifacts ?? []}
            renderRow={(rowData) => [
              <GridCellSmallFadedText>
                {`${new Date(rowData.run_at).toISOString().slice(0, 10)} @ ${new Date(rowData.run_at).toLocaleTimeString()}`}
              </GridCellSmallFadedText>,
              <GridCellNormalText>
                {rowData.dependent_variable}
              </GridCellNormalText>,
              <GridCellNormalText>{rowData.variables}</GridCellNormalText>,
              <GridCellNormalText>
                <VerySmallNormalTag>{rowData.model}</VerySmallNormalTag>
              </GridCellNormalText>,
              <GridCellNormalText>{rowData?.fit_score}</GridCellNormalText>,
              <GridCellNormalText>{rowData.run_by.name}</GridCellNormalText>,
            ]}
            headerInfo={gridHeader}
            maxDisplayedItems={PAGE_SIZE}
            serverSide
            serverTotal={total}
            serverPage={page}
            onPageChange={handlePageChange}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            loading={loading}
            loadingMessage={'Loading run history...'}
          />
        </SimpleAccordion.Panel>
      </SimpleAccordion>
    </>
  )
}

export default ModelHistoryPage
